import React, { useMemo, useState } from "react";
import { Box, Spinner, Select, Link } from "@amzn/awsui-components-react";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const QUARTERS = [
  { id: "Q1", label: "Q1", range: "Jan – Mar", months: [0, 1, 2] },
  { id: "Q2", label: "Q2", range: "Apr – Jun", months: [3, 4, 5] },
  { id: "Q3", label: "Q3", range: "Jul – Sep", months: [6, 7, 8] },
  { id: "Q4", label: "Q4", range: "Oct – Dec", months: [9, 10, 11] },
];

const quarterOf = (monthIdx) => Math.floor(monthIdx / 3); // 0..3

/**
 * Training calendar matching the prototype: a quarter selector row across the
 * top (with per-quarter session counts), then the selected quarter's three
 * months shown side by side as columns. Each session is a card with a date
 * block, title, region/time lines with delivered/upcoming status dots, and
 * platform + recording pills. A platform filter narrows the sessions.
 */
const TrainingCalendar = ({ calendar }) => {
  const sessions = (calendar && calendar.sessions) || [];
  const platforms = (calendar && calendar.platforms) || [];

  const [platformFilter, setPlatformFilter] = useState({
    label: "All platforms",
    value: "ALL",
  });

  // Years present in the data (plus the current year so the picker is never
  // empty), newest first.
  const years = useMemo(() => {
    const set = new Set();
    sessions.forEach((s) => {
      const y = new Date(s.session_date).getFullYear();
      if (!Number.isNaN(y)) set.add(y);
    });
    set.add(new Date().getFullYear());
    return Array.from(set).sort((a, b) => b - a);
  }, [sessions]);

  // Default to the current year if it has sessions, otherwise the most recent
  // year that has any.
  const defaultYear = useMemo(() => {
    const thisYear = new Date().getFullYear();
    const hasThisYear = sessions.some(
      (s) => new Date(s.session_date).getFullYear() === thisYear
    );
    if (hasThisYear) return thisYear;
    return years[0] || thisYear;
  }, [sessions, years]);

  const [yearFilter, setYearFilter] = useState({
    label: String(defaultYear),
    value: String(defaultYear),
  });
  // Keep the selected year valid as data loads in.
  React.useEffect(() => {
    setYearFilter({ label: String(defaultYear), value: String(defaultYear) });
  }, [defaultYear]);

  const yearOptions = useMemo(
    () => years.map((y) => ({ label: String(y), value: String(y) })),
    [years]
  );

  // Apply the year filter first, then platform, so every downstream count and
  // month column reflects only the selected year.
  const filtered = useMemo(() => {
    return sessions.filter((s) => {
      if (String(new Date(s.session_date).getFullYear()) !== yearFilter.value) {
        return false;
      }
      if (platformFilter.value === "ALL") return true;
      return String(s.platform_id) === platformFilter.value;
    });
  }, [sessions, platformFilter, yearFilter]);

  // Per-quarter counts (of the filtered set) for the selector cards.
  const quarterCounts = useMemo(() => {
    const counts = [0, 0, 0, 0];
    filtered.forEach((s) => {
      const q = quarterOf(new Date(s.session_date).getMonth());
      counts[q] += 1;
    });
    return counts;
  }, [filtered]);

  // Default to the quarter with the earliest upcoming session, else the first
  // quarter that has any sessions.
  const defaultQuarter = useMemo(() => {
    const nowTs = Date.now();
    const upcoming = filtered
      .filter((s) => new Date(s.session_date) >= nowTs)
      .sort((a, b) => new Date(a.session_date) - new Date(b.session_date))[0];
    if (upcoming) return `Q${quarterOf(new Date(upcoming.session_date).getMonth()) + 1}`;
    const firstWithData = quarterCounts.findIndex((c) => c > 0);
    return `Q${(firstWithData < 0 ? 0 : firstWithData) + 1}`;
  }, [filtered, quarterCounts]);

  const [activeQuarter, setActiveQuarter] = useState(defaultQuarter);
  // Keep the selection sensible if the filter changes what's available.
  React.useEffect(() => setActiveQuarter(defaultQuarter), [defaultQuarter]);

  // Counts in the platform picker reflect the currently selected year only.
  const yearSessions = useMemo(
    () =>
      sessions.filter(
        (s) => String(new Date(s.session_date).getFullYear()) === yearFilter.value
      ),
    [sessions, yearFilter]
  );

  const platformOptions = useMemo(
    () => [
      { label: `All platforms · ${yearSessions.length} sessions`, value: "ALL" },
      ...platforms.map((p) => ({
        label: `${p.display_name} · ${
          yearSessions.filter((s) => String(s.platform_id) === String(p.platform_id))
            .length
        }`,
        value: String(p.platform_id),
      })),
    ],
    [platforms, yearSessions]
  );

  if (!calendar) {
    return (
      <Box padding="l">
        <Spinner /> Loading calendar...
      </Box>
    );
  }

  const quarter = QUARTERS.find((q) => q.id === activeQuarter) || QUARTERS[0];
  const now = new Date();
  // Only highlight the current month as "NEXT UP" when we're actually looking
  // at the current year.
  const currentMonthIdx =
    yearFilter.value === String(now.getFullYear()) ? now.getMonth() : -1;

  return (
    <div className="raise-cal">
      <Box variant="p" padding={{ bottom: "s" }}>
        Get visibility into past and upcoming trainings, quarter by quarter.
        Recordings are linked where one has been published.
      </Box>

      <div className="raise-cal-filter">
        <span className="raise-cal-filter-label">YEAR</span>
        <Select
          selectedOption={yearFilter}
          onChange={({ detail }) => setYearFilter(detail.selectedOption)}
          options={yearOptions}
          selectedAriaLabel="Selected"
        />
        <span className="raise-cal-filter-label">FILTER BY PLATFORM</span>
        <Select
          selectedOption={platformFilter}
          onChange={({ detail }) => setPlatformFilter(detail.selectedOption)}
          options={platformOptions}
          selectedAriaLabel="Selected"
        />
      </div>

      {/* Quarter selector */}
      <div className="raise-cal-quarters">
        {QUARTERS.map((q, i) => (
          <button
            key={q.id}
            type="button"
            className={`raise-cal-quarter ${q.id === activeQuarter ? "active" : ""}`}
            onClick={() => setActiveQuarter(q.id)}
          >
            <div className="raise-cal-quarter-label">{q.label}</div>
            <div className="raise-cal-quarter-range">{q.range}</div>
            <div className="raise-cal-quarter-count">
              {quarterCounts[i]} session{quarterCounts[i] === 1 ? "" : "s"}
            </div>
          </button>
        ))}
      </div>

      {/* Delivered/Upcoming legend */}
      <div className="raise-cal-legend">
        <span className="raise-cal-legend-item">
          <span className="raise-dot delivered" /> Delivered
        </span>
        <span className="raise-cal-legend-item">
          <span className="raise-dot upcoming" /> Upcoming
        </span>
      </div>

      {/* The selected quarter's three months, side by side */}
      <div className="raise-cal-months">
        {quarter.months.map((monthIdx) => {
          const monthSessions = filtered
            .filter((s) => new Date(s.session_date).getMonth() === monthIdx)
            .sort(
              (a, b) =>
                new Date(a.session_date) - new Date(b.session_date) ||
                (a.sort_order || 0) - (b.sort_order || 0)
            );
          const isCurrent = monthIdx === currentMonthIdx;
          return (
            <div className="raise-cal-month" key={monthIdx}>
              <div className="raise-cal-month-head">
                <span>{MONTH_NAMES[monthIdx]}</span>
                {isCurrent ? <span className="raise-cal-nextup">NEXT UP</span> : null}
              </div>
              {monthSessions.length === 0 ? (
                <div className="raise-cal-empty">No sessions</div>
              ) : (
                monthSessions.map((s) => <SessionCard key={s.session_id} session={s} />)
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** A single session card: date block + details. */
const SessionCard = ({ session }) => {
  const date = new Date(session.session_date);
  const day = date.getDate();
  const weekday = WEEKDAYS[date.getDay()];
  // Delivered vs upcoming is derived purely from the date: a session before
  // today is delivered. There is no stored status.
  const delivered = date < new Date();

  // Title may carry a "· both sittings" style suffix in the prototype; keep the
  // full title as stored.
  return (
    <div className="raise-cal-session">
      <div className="raise-cal-date">
        <div className="raise-cal-day">{day}</div>
        <div className="raise-cal-weekday">{weekday}</div>
      </div>
      <div className="raise-cal-session-body">
        <div className="raise-cal-session-title">{session.title}</div>
        {session.region_label ? (
          <div className="raise-cal-session-region">
            <span className={`raise-dot ${delivered ? "delivered" : "upcoming"}`} />
            <span>{`${date.getDate()} ${MONTH_NAMES[date.getMonth()].slice(0, 3)}`}</span>
            <span className="raise-cal-region-text">{session.region_label}</span>
          </div>
        ) : null}
        <div className="raise-cal-pills">
          {session.platform_name ? (
            <span className="raise-pill platform">{session.platform_name}</span>
          ) : null}
          {session.recording_url ? (
            <Link href={session.recording_url} external>
              <span className="raise-pill recording">Watch the recording →</span>
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TrainingCalendar;
